import { useEffect, useMemo, useRef, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useProducts } from "@/context/ProductsContext";
import { api, API, formatErr } from "@/lib/api";
import { formatPrice, parseSwatch } from "@/data/products";
import { toast } from "sonner";
import { Plus, Trash2, Save, ArrowLeft, ArrowRight, X, Check, Search, Edit3, Copy, Eye } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const abs = (url) => (!url ? url : url.startsWith("http") ? url : url.startsWith("/api/") ? API.replace(/\/api$/, "") + url : url);
const slugify = (s) => (s || "").toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

const SECTIONS = ["women", "menswear", "men", "bridal", "festive", "cocktail", "new"];
const STANDARD_SIZES = ["XS", "S", "M", "L", "XL", "XXL"];

const EMPTY = {
  id: null, slug: "", title: "", subtitle: "", price: 0,
  category: "lehengas",
  section: [], colors: [], sizes: [],
  images: [], fabric: "", craftsmanship: "", delivery: "",
  description: "", tag: "", home_order: "", is_active: true,
};

export default function AdminProducts() {
  const { user } = useAuth();
  const { refresh, categories } = useProducts();
  const [list, setList] = useState([]);
  const [media, setMedia] = useState([]);
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null); // product being edited (or EMPTY for new)
  const [showPicker, setShowPicker] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const uploadRef = useRef(null);

  const load = async () => {
    try {
      const [p, m] = await Promise.all([api.get("/admin/products"), api.get("/media")]);
      setList(p.data); setMedia(m.data);
    } catch (e) { toast.error(formatErr(e)); }
    setLoading(false);
  };
  useEffect(() => { if (user?.role === "admin") load(); }, [user]);

  const filtered = useMemo(() =>
    list.filter(p => !q || (p.title + " " + p.category + " " + p.subtitle).toLowerCase().includes(q.toLowerCase())),
  [list, q]);

  if (user === undefined) return <div className="pt-40 container-lux">Loading…</div>;
  if (!user) return <Navigate to="/login?next=/admin/products" replace />;
  if (user.role !== "admin") return (
    <div className="pt-40 container-lux min-h-[60vh]">
      <p className="overline text-muted-foreground">Restricted</p>
      <h1 className="mt-4 font-serif text-4xl">Admin access required</h1>
    </div>
  );

  const openNew = () => setEditing({ ...EMPTY });
  const openEdit = (p) => setEditing({ ...EMPTY, ...p, tag: p.tag || "" });
  const close = () => setEditing(null);

  const save = async () => {
    if (!editing) return;
    const slug = editing.slug || slugify(editing.title);
    if (!editing.title) return toast.error("Title is required");
    if (!slug) return toast.error("Slug is required");
    if (!editing.price || editing.price <= 0) return toast.error("Price must be greater than zero");

    const payload = {
      slug,
      title: editing.title.trim(),
      subtitle: editing.subtitle || "",
      price: Number(editing.price),
      category: editing.category,
      section: editing.section || [],
      colors: editing.colors || [],
      sizes: editing.sizes || [],
      images: (editing.images || []).map(u => {
        // if this URL is in the media library, store as relative "/api/media/{id}"
        const m = media.find(x => `${API}/media/${x.id}` === abs(u) || `/api/media/${x.id}` === u);
        return m ? `/api/media/${m.id}` : u;
      }),
      fabric: editing.fabric || "",
      craftsmanship: editing.craftsmanship || "",
      delivery: editing.delivery || "",
      description: editing.description || "",
      tag: (editing.tag || "").trim() || null,
      home_order: editing.home_order === "" || editing.home_order == null ? null : Number(editing.home_order),
      is_active: editing.is_active !== false,
    };
    setSaving(true);
    try {
      if (editing.id) {
        await api.put(`/admin/products/${editing.id}`, payload);
        toast.success("Product updated");
      } else {
        const { data } = await api.post("/admin/products", payload);
        toast.success("Product created");
        setEditing({ ...EMPTY, ...data, tag: data.tag || "" });
      }
      await Promise.all([load(), refresh()]);
    } catch (e) { toast.error(formatErr(e)); }
    setSaving(false);
  };

  const del = async () => {
    if (!editing?.id) return;
    if (!confirm(`Delete "${editing.title}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/products/${editing.id}`);
      toast("Product deleted");
      close();
      await Promise.all([load(), refresh()]);
    } catch (e) { toast.error(formatErr(e)); }
  };

  const duplicate = async (row) => {
    const src = row || editing;
    if (!src?.id) return;
    try {
      const { data } = await api.post(`/admin/products/${src.id}/duplicate`);
      toast.success("Duplicated as draft");
      await Promise.all([load(), refresh()]);
      openEdit(data); // open the fresh copy immediately
    } catch (e) { toast.error(formatErr(e)); }
  };

  const preview = (p) => {
    const target = p || editing;
    if (!target?.slug) return;
    window.open(`/product/${target.slug}?preview=1`, "_blank", "noopener");
  };

  // helpers for editing
  const set = (k, v) => setEditing({ ...editing, [k]: v });
  const toggleArr = (k, v) => {
    const arr = editing[k] || [];
    set(k, arr.includes(v) ? arr.filter(x => x !== v) : [...arr, v]);
  };
  const setCsv = (k, csv) => set(k, csv.split(",").map(s => s.trim()).filter(Boolean));
  const move = (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= editing.images.length) return;
    const arr = editing.images.slice();
    [arr[idx], arr[j]] = [arr[j], arr[idx]];
    set("images", arr);
  };
  const removeImg = (idx) => set("images", editing.images.filter((_, i) => i !== idx));
  const addImgFromLib = (id) => {
    const url = `/api/media/${id}`;
    if ((editing.images || []).includes(url) || (editing.images || []).includes(abs(url))) return;
    set("images", [...(editing.images || []), url]);
  };

  const uploadAndAttach = async (files) => {
    if (!files?.length) return;
    setUploading(true);
    let attached = [...(editing.images || [])];
    let ok = 0, fail = 0;
    for (const f of files) {
      try {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/media/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        attached = [...attached, `/api/media/${data.id}`];
        ok++;
      } catch (e) { fail++; toast.error(`${f.name}: ${formatErr(e)}`); }
    }
    // refresh library so pickers show new items too
    try { const m = await api.get("/media"); setMedia(m.data); } catch {}
    setEditing((prev) => prev ? { ...prev, images: attached } : prev);
    setUploading(false);
    if (ok) toast.success(`Uploaded & attached ${ok} file${ok>1?"s":""}${fail?` (${fail} failed)`:""}`);
    if (uploadRef.current) uploadRef.current.value = "";
  };

  return (
    <div className="pt-32 pb-24 bg-ivory min-h-screen" data-testid="admin-cms-page">
      <div className="container-lux">
        <div className="flex items-baseline justify-between gap-4 flex-wrap">
          <div>
            <p className="overline text-muted-foreground">Atelier · Admin</p>
            <h1 className="mt-4 font-serif tracking-tighter text-5xl md:text-6xl font-light">Product CMS</h1>
            <p className="mt-3 text-sm text-muted-foreground max-w-xl">Create, edit and price every piece in the house. Save changes to publish instantly to the storefront.</p>
          </div>
          <div className="flex gap-3">
            <Link to="/admin/media" className="lux-btn-outline py-3 px-5 text-[10px]">Media Library</Link>
            <button onClick={openNew} className="lux-btn-dark py-3 px-5 text-[10px] inline-flex items-center gap-2" data-testid="new-product-btn"><Plus size={13} strokeWidth={1} /> New Product</button>
          </div>
        </div>

        <div className="mt-12">
          <div className="relative max-w-md">
            <Search size={14} strokeWidth={1} className="absolute left-0 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input value={q} onChange={(e)=>setQ(e.target.value)} placeholder="Search products" data-testid="cms-search" className="lux-input pl-6" />
          </div>

          <div className="mt-8 border border-border bg-ivory overflow-hidden">
            <div className="grid grid-cols-[80px_1fr_160px_120px_100px_140px] gap-4 px-6 py-4 border-b border-border bg-sand/40 overline text-muted-foreground">
              <span>Image</span><span>Piece</span><span>Category</span><span>Price</span><span>Status</span><span className="text-right">Actions</span>
            </div>
            {loading ? (
              <p className="px-6 py-10 text-sm text-muted-foreground">Loading catalog…</p>
            ) : filtered.length === 0 ? (
              <p className="px-6 py-10 text-sm text-muted-foreground">No products yet. Click <strong>New Product</strong> to create the first piece.</p>
            ) : filtered.map(p => (
              <div key={p.id} className="grid grid-cols-[80px_1fr_160px_120px_100px_140px] gap-4 px-6 py-4 items-center border-b border-border/60 hover:bg-sand/20 transition-colors" data-testid={`cms-row-${p.slug}`}>
                <div className="w-14 h-16 bg-muted overflow-hidden">
                  {p.images?.[0] && <img src={abs(p.images[0])} alt="" className="w-full h-full object-cover" />}
                </div>
                <div>
                  <p className="font-serif text-lg leading-tight">{p.title}</p>
                  <p className="text-[11px] text-muted-foreground mt-1">{p.subtitle}</p>
                </div>
                <span className="text-xs uppercase tracking-widest text-muted-foreground">{p.category}</span>
                <span className="text-sm">{formatPrice(p.price)}</span>
                <span className={`text-[10px] uppercase tracking-widest ${p.is_active ? "text-ink" : "text-muted-foreground"}`}>{p.is_active ? "Published" : "Draft"}</span>
                <div className="flex justify-end gap-1">
                  <button onClick={()=>preview(p)} className="p-2 hover:bg-muted" title="Preview" data-testid={`preview-${p.slug}`}><Eye size={14} strokeWidth={1} /></button>
                  <button onClick={()=>duplicate(p)} className="p-2 hover:bg-muted" title="Duplicate" data-testid={`duplicate-${p.slug}`}><Copy size={14} strokeWidth={1} /></button>
                  <button onClick={()=>openEdit(p)} className="p-2 hover:bg-muted" title="Edit" data-testid={`edit-${p.slug}`}><Edit3 size={14} strokeWidth={1} /></button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Editor */}
      <Dialog open={!!editing} onOpenChange={(v)=>!v && close()}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto bg-ivory border border-border rounded-none p-0">
          <div className="p-8 border-b border-border sticky top-0 bg-ivory z-10 flex items-center justify-between">
            <div>
              <p className="overline text-muted-foreground">{editing?.id ? "Editing" : "New Product"}</p>
              <DialogHeader><DialogTitle className="font-serif text-3xl md:text-4xl tracking-tighter font-light mt-2">{editing?.title || "Untitled Piece"}</DialogTitle></DialogHeader>
            </div>
            <div className="flex gap-2">
              {editing?.id && (
                <>
                  <button onClick={()=>preview()} className="lux-btn-outline py-3 px-5 text-[10px] inline-flex items-center gap-2" data-testid="preview-btn"><Eye size={12} strokeWidth={1} /> Preview</button>
                  <button onClick={()=>duplicate()} className="lux-btn-outline py-3 px-5 text-[10px] inline-flex items-center gap-2" data-testid="duplicate-btn"><Copy size={12} strokeWidth={1} /> Duplicate</button>
                  <button onClick={del} className="lux-btn-outline py-3 px-5 text-[10px] inline-flex items-center gap-2" data-testid="delete-product"><Trash2 size={12} strokeWidth={1} /> Delete</button>
                </>
              )}
              <button onClick={close} className="lux-btn-outline py-3 px-5 text-[10px]">Close</button>
              <button onClick={save} disabled={saving} className="lux-btn-dark py-3 px-5 text-[10px] inline-flex items-center gap-2" data-testid="save-product"><Save size={12} strokeWidth={1} /> {saving ? "Saving…" : "Save"}</button>
            </div>
          </div>

          {editing && (
          <div className="p-8 grid md:grid-cols-2 gap-8">
            {/* Left: Text fields */}
            <div className="space-y-6">
              <Field label="Title" testid="f-title" value={editing.title} onChange={(e)=>set("title", e.target.value)} onBlur={()=>{ if(!editing.slug) set("slug", slugify(editing.title)); }} />
              <Field label="Subtitle" testid="f-subtitle" value={editing.subtitle} onChange={(e)=>set("subtitle", e.target.value)} />
              <div className="grid grid-cols-2 gap-4">
                <Field label="Slug" testid="f-slug" value={editing.slug} onChange={(e)=>set("slug", slugify(e.target.value))} />
                <Field label="Tag" placeholder="New, Couture…" testid="f-tag" value={editing.tag} onChange={(e)=>set("tag", e.target.value)} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <Field label="Price (INR)" testid="f-price" type="number" min="0" step="100" value={editing.price} onChange={(e)=>set("price", e.target.value)} />
                <div>
                  <label className="overline text-muted-foreground">Category</label>
                  <select className="lux-input" data-testid="f-category" value={editing.category} onChange={(e)=>set("category", e.target.value)}>
                    {categories.map(c => <option key={c.slug} value={c.slug}>{c.name}</option>)}
                  </select>
                </div>
              </div>

              <div>
                <label className="overline text-muted-foreground">Sections</label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {SECTIONS.map(s => (
                    <button type="button" key={s} onClick={()=>toggleArr("section", s)} data-testid={`sec-${s}`}
                      className={`px-3 py-2 text-[10px] uppercase tracking-widest border transition-colors ${editing.section?.includes(s) ? "bg-ink text-ivory border-ink" : "border-border hover:border-ink"}`}>{s}</button>
                  ))}
                </div>
              </div>

              <ColorPicker value={editing.colors || []} onChange={(v) => set("colors", v)} />

              <div>
                <label className="overline text-muted-foreground">Sizes</label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {STANDARD_SIZES.map(s => (
                    <button
                      type="button"
                      key={s}
                      onClick={()=>toggleArr("sizes", s)}
                      data-testid={`size-${s}`}
                      className={`px-4 py-2 text-[11px] uppercase tracking-widest border transition-colors ${(editing.sizes||[]).includes(s) ? "bg-ink text-ivory border-ink" : "border-border hover:border-ink"}`}
                    >{s}</button>
                  ))}
                </div>
                <div className="mt-3">
                  <Field
                    label="Additional sizes (comma separated · e.g. 38, 40, 42, Free)"
                    testid="f-sizes-extra"
                    value={(editing.sizes||[]).filter(x => !STANDARD_SIZES.includes(x)).join(", ")}
                    onChange={(e)=>{
                      const extras = e.target.value.split(",").map(s => s.trim()).filter(Boolean);
                      const std = (editing.sizes||[]).filter(x => STANDARD_SIZES.includes(x));
                      set("sizes", [...std, ...extras]);
                    }}
                    placeholder="38, 40, 42, Free"
                  />
                </div>
              </div>

              <TextArea label="Description" testid="f-desc" rows={4} value={editing.description} onChange={(e)=>set("description", e.target.value)} placeholder="The full couture story of the piece — worn, styled, and remembered." />
              <TextArea label="Fabric" testid="f-fabric" rows={2} value={editing.fabric} onChange={(e)=>set("fabric", e.target.value)} />
              <TextArea label="Components" testid="f-craft" rows={2} value={editing.craftsmanship} onChange={(e)=>set("craftsmanship", e.target.value)} />
              <Field label="Delivery Timeline" testid="f-delivery" value={editing.delivery} onChange={(e)=>set("delivery", e.target.value)} placeholder="10-12 weeks" />
              <Field label="Home Order · lower shows first (blank = end)" testid="f-home-order" type="number" min="0" step="1" value={editing.home_order ?? ""} onChange={(e)=>set("home_order", e.target.value)} placeholder="1, 2, 3…" />
              <label className="flex items-center gap-3 text-sm">
                <input type="checkbox" checked={editing.is_active !== false} onChange={(e)=>set("is_active", e.target.checked)} data-testid="f-active" />
                Published on storefront
              </label>
            </div>

            {/* Right: Imagery */}
            <div>
              <div className="flex items-baseline justify-between flex-wrap gap-2">
                <label className="overline">Imagery · {(editing.images||[]).length}</label>
                <div className="flex items-center gap-4">
                  <button type="button" onClick={()=>uploadRef.current?.click()} disabled={uploading} className="lux-link inline-flex items-center gap-1" data-testid="upload-attach">
                    <Plus size={11} strokeWidth={1} /> {uploading ? "Uploading…" : "Upload from PC"}
                  </button>
                  <button type="button" onClick={()=>setShowPicker(true)} className="lux-link" data-testid="open-picker">From library →</button>
                </div>
                <input
                  ref={uploadRef}
                  type="file"
                  multiple
                  accept="image/*"
                  className="hidden"
                  onChange={(e)=>uploadAndAttach(Array.from(e.target.files || []))}
                  data-testid="pc-file-input"
                />
              </div>
              {(editing.images||[]).length === 0 ? (
                <div className="mt-4 border border-dashed border-border p-10 text-center text-xs text-muted-foreground">
                  No imagery attached. Open the library or paste an external image URL below.
                </div>
              ) : (
                <ul className="mt-4 grid grid-cols-2 gap-4">
                  {editing.images.map((u, idx) => (
                    <li key={u+idx} className="relative">
                      <div className="aspect-[3/4] bg-muted overflow-hidden border border-border"><img src={abs(u)} alt="" className="w-full h-full object-cover" /></div>
                      <div className="mt-2 flex items-center justify-between text-[10px] uppercase tracking-widest">
                        <span className="text-muted-foreground">{idx === 0 ? "Primary" : idx === 1 ? "Hover" : `#${idx+1}`}</span>
                        <div className="flex items-center gap-1">
                          <button onClick={()=>move(idx,-1)} disabled={idx===0} className="p-1 disabled:opacity-30"><ArrowLeft size={12} strokeWidth={1} /></button>
                          <button onClick={()=>move(idx,1)} disabled={idx===editing.images.length-1} className="p-1 disabled:opacity-30"><ArrowRight size={12} strokeWidth={1} /></button>
                          <button onClick={()=>removeImg(idx)} className="p-1 text-destructive" data-testid={`rm-img-${idx}`}><X size={12} strokeWidth={1} /></button>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}

              <div className="mt-6">
                <label className="overline text-muted-foreground">Add external URL</label>
                <div className="flex gap-2">
                  <input id="ext-url" placeholder="https://…" className="lux-input flex-1" data-testid="f-ext-url" />
                  <button type="button" className="lux-btn-outline py-3 px-4 text-[10px]"
                    onClick={()=>{
                      const el = document.getElementById("ext-url");
                      const v = el.value.trim();
                      if (!v) return;
                      set("images", [...(editing.images||[]), v]);
                      el.value = "";
                    }}
                  >Add</button>
                </div>
              </div>
            </div>
          </div>)}
        </DialogContent>
      </Dialog>

      {/* Media picker */}
      <Dialog open={showPicker} onOpenChange={setShowPicker}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto bg-ivory border border-border rounded-none">
          <DialogHeader><DialogTitle className="font-serif text-3xl font-light tracking-tighter">Media Library</DialogTitle></DialogHeader>
          {media.length === 0 ? (
            <p className="text-sm text-muted-foreground mt-6">Library empty. <Link to="/admin/media" className="underline">Upload files</Link> first.</p>
          ) : (
            <ul className="mt-6 grid grid-cols-3 md:grid-cols-5 gap-4">
              {media.filter(m => m.content_type?.startsWith("image/")).map(m => {
                const attached = (editing?.images || []).includes(`/api/media/${m.id}`) || (editing?.images || []).includes(`${API}/media/${m.id}`);
                return (
                  <button key={m.id} type="button" onClick={()=>{ addImgFromLib(m.id); }}
                    data-testid={`pick-lib-${m.id}`}
                    className={`relative aspect-square overflow-hidden border transition-colors ${attached ? "border-ink ring-2 ring-ink" : "border-border hover:border-ink"}`}>
                    <img src={`${API}/media/${m.id}`} alt="" className="w-full h-full object-cover" loading="lazy" />
                    {attached && <span className="absolute top-2 right-2 w-6 h-6 bg-ink text-ivory flex items-center justify-center"><Check size={12} strokeWidth={1.5} /></span>}
                  </button>
                );
              })}
            </ul>
          )}
          <div className="mt-8 flex justify-end">
            <button onClick={()=>setShowPicker(false)} className="lux-btn-dark py-3 px-5 text-[10px]">Done</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ label, testid, ...rest }) {
  return (
    <div>
      <label className="overline text-muted-foreground">{label}</label>
      <input className="lux-input" data-testid={testid} {...rest} />
    </div>
  );
}

function ColorPicker({ value, onChange }) {
  const [hex, setHex] = useState("#B79764");
  const [name, setName] = useState("");
  const add = () => {
    const cleanHex = (hex || "").trim();
    const cleanName = (name || "").trim();
    if (!cleanHex && !cleanName) return;
    const entry = cleanName ? `${cleanName}|${cleanHex || "#CCCCCC"}` : (cleanHex || cleanName);
    onChange([...(value || []), entry]);
    setName("");
  };
  const remove = (i) => onChange(value.filter((_, k) => k !== i));
  const move = (i, dir) => {
    const j = i + dir; if (j < 0 || j >= value.length) return;
    const next = value.slice(); [next[i], next[j]] = [next[j], next[i]];
    onChange(next);
  };
  return (
    <div>
      <label className="overline text-muted-foreground">Colours</label>
      <ul className="mt-3 flex flex-wrap gap-2 min-h-[42px]" data-testid="colors-chips">
        {(value || []).length === 0 && <li className="text-xs text-muted-foreground italic self-center">No colours added yet</li>}
        {(value || []).map((c, i) => {
          const p = parseSwatch(c);
          return (
            <li key={i} className="inline-flex items-center gap-2 border border-border pl-1 pr-2 py-1" data-testid={`color-chip-${i}`}>
              <span className="w-6 h-6 border border-black/10" style={{ background: p.hex }} />
              <span className="text-xs">{p.name && p.name !== p.hex ? p.name : ""}</span>
              <span className="text-[10px] text-muted-foreground font-mono">{p.hex.toUpperCase()}</span>
              <button type="button" onClick={()=>move(i, -1)} disabled={i===0} className="p-0.5 disabled:opacity-30" title="Move left" data-testid={`color-up-${i}`}><ArrowLeft size={11} strokeWidth={1} /></button>
              <button type="button" onClick={()=>move(i, 1)} disabled={i===value.length-1} className="p-0.5 disabled:opacity-30" title="Move right" data-testid={`color-down-${i}`}><ArrowRight size={11} strokeWidth={1} /></button>
              <button type="button" onClick={()=>remove(i)} className="p-1 text-muted-foreground hover:text-destructive" data-testid={`color-x-${i}`}><X size={11} strokeWidth={1} /></button>
            </li>
          );
        })}
      </ul>
      <div className="mt-4 grid grid-cols-[64px_1fr_auto] gap-3 items-end">
        <div>
          <label className="overline text-muted-foreground">Swatch</label>
          <input type="color" value={hex} onChange={(e)=>setHex(e.target.value)} className="mt-2 w-full h-11 border border-border p-0 bg-transparent cursor-pointer" data-testid="color-hex" />
        </div>
        <div>
          <label className="overline text-muted-foreground">Name · optional</label>
          <input value={name} onChange={(e)=>setName(e.target.value)} placeholder="ivory, blush, custom…" className="lux-input" data-testid="color-name" onKeyDown={(e)=>{ if(e.key==='Enter'){ e.preventDefault(); add(); } }} />
        </div>
        <button type="button" onClick={add} className="lux-btn-outline py-3 px-4 text-[10px]" data-testid="color-add">Add</button>
      </div>
    </div>
  );
}
function TextArea({ label, testid, rows = 3, ...rest }) {
  return (
    <div>
      <label className="overline text-muted-foreground">{label}</label>
      <textarea rows={rows} data-testid={testid} className="w-full bg-transparent border border-border p-3 text-sm focus:outline-none focus:border-ink" {...rest} />
    </div>
  );
}
